import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, Scale, Globe, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO, { createBreadcrumbSchema } from "../components/SEO";

export default function TermosPage() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'pt';
  const lastUpdate = currentLang === 'en' ? "January 23, 2026" : "23 de Janeiro de 2026";

  const paths = {
    home: '/',
    about: currentLang === 'en' ? '/en/about' : '/pt/sobre',
    privacy: currentLang === 'en' ? '/en/privacy-policy' : '/pt/politica-de-privacidade',
    contact: currentLang === 'en' ? '/en/contact' : '/pt/contato',
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const seoConfig = currentLang === 'en' ? {
    title: "Terms of Use - OmbudNow",
    description: "OmbudNow Terms of Use. Read our service terms, user responsibilities, and usage conditions for our whistleblowing and ombudsman platform.",
    canonical: "/en/terms-of-use"
  } : {
    title: "Termos de Uso - OmbudNow",
    description: "Termos de Uso da OmbudNow. Leia os termos de serviço, responsabilidades do usuário e condições de uso da plataforma de denúncias e ouvidoria.",
    canonical: "/pt/termos-de-uso"
  };

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: currentLang === 'en' ? 'Home' : 'Início', url: '/' },
    { name: currentLang === 'en' ? 'Terms of Use' : 'Termos de Uso', url: seoConfig.canonical }
  ]);

  const content = currentLang === 'en' ? {
    badge: "Legal Document",
    title: "Terms of Use",
    lastUpdate: "Last updated",
    sections: [
      {
        title: "1. Acceptance of Terms",
        content: "By accessing or using the OmbudNow platform, you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our services. Continued use of the platform after any modifications constitutes your acceptance of the revised terms."
      },
      {
        title: "2. Description of Service",
        content: "OmbudNow provides a SaaS platform for compliance management, whistleblowing, and ombudsman services. Our services include: secure and anonymous report submission channels; case management and investigation workflows; compliance dashboards and analytics; multi-tenant isolated environments for each organization; integration with existing corporate systems."
      },
      {
        title: "3. User Registration and Accounts",
        content: "To access certain features, you must create an account providing accurate and complete information. You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. You agree to notify OmbudNow immediately of any unauthorized use of your account."
      },
      {
        title: "4. User Responsibilities",
        content: "Users agree to: use the platform only for lawful purposes and in accordance with these Terms; not attempt to gain unauthorized access to any part of the platform; not transmit any malicious code or content; maintain the confidentiality of reports and sensitive information; comply with all applicable laws and regulations related to whistleblowing and data protection."
      },
      {
        title: "5. Intellectual Property",
        content: "All content, features, and functionality of the OmbudNow platform, including but not limited to text, graphics, logos, and software, are the exclusive property of OmbudNow and are protected by international copyright, trademark, and other intellectual property laws."
      },
      {
        title: "6. Subscriptions and Payments",
        content: "Certain features require a paid subscription. Pricing, payment terms, and plan details are described on our pricing page. All fees are non-refundable unless otherwise stated. OmbudNow reserves the right to modify pricing with 30 days prior notice. Free trial periods, when offered, automatically convert to paid subscriptions unless cancelled before the trial expires."
      },
      {
        title: "7. Data Protection and Privacy",
        content: "Your use of the platform is also governed by our Privacy Policy. We are committed to complying with the Brazilian General Data Protection Law (LGPD) and other applicable privacy regulations. By using the platform, you consent to data processing as described in our Privacy Policy."
      },
      {
        title: "8. Limitation of Liability",
        content: "OmbudNow shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the platform. Our total liability for any claim shall not exceed the amount paid by you in the twelve months preceding the claim. We do not guarantee uninterrupted or error-free service."
      },
      {
        title: "9. Termination",
        content: "OmbudNow reserves the right to suspend or terminate your access to the platform at any time, with or without cause, upon reasonable notice. Upon termination, your right to use the platform ceases immediately. Provisions that by their nature should survive termination will remain in effect."
      },
      {
        title: "10. Contact",
        content: "For questions regarding these Terms of Use, please contact us at: support@ombudnow.com"
      }
    ]
  } : {
    badge: "Documento Legal",
    title: "Termos de Uso",
    lastUpdate: "Última atualização",
    sections: [
      {
        title: "1. Aceitação dos Termos",
        content: "Ao acessar ou utilizar a plataforma OmbudNow, você concorda em estar vinculado a estes Termos de Uso. Caso não concorde com estes termos, por favor, não utilize nossos serviços. O uso continuado da plataforma após quaisquer modificações constitui sua aceitação dos termos revisados."
      },
      {
        title: "2. Descrição do Serviço",
        content: "A OmbudNow fornece uma plataforma SaaS para gestão de compliance, canal de denúncias e ouvidoria. Nossos serviços incluem: canais seguros e anônimos para envio de denúncias; fluxos de gestão de casos e investigações; dashboards de compliance e análises; ambientes multi-tenant isolados para cada organização; integração com sistemas corporativos existentes."
      },
      {
        title: "3. Cadastro e Contas de Usuário",
        content: "Para acessar determinadas funcionalidades, é necessário criar uma conta fornecendo informações precisas e completas. Você é responsável por manter a confidencialidade de suas credenciais de acesso e por todas as atividades realizadas em sua conta. Você concorda em notificar a OmbudNow imediatamente sobre qualquer uso não autorizado de sua conta."
      },
      {
        title: "4. Responsabilidades do Usuário",
        content: "Os usuários concordam em: utilizar a plataforma apenas para fins lícitos e de acordo com estes Termos; não tentar obter acesso não autorizado a qualquer parte da plataforma; não transmitir código malicioso ou conteúdo prejudicial; manter a confidencialidade das denúncias e informações sensíveis; cumprir todas as leis e regulamentações aplicáveis relacionadas a denúncias e proteção de dados."
      },
      {
        title: "5. Propriedade Intelectual",
        content: "Todo o conteúdo, funcionalidades e recursos da plataforma OmbudNow, incluindo, mas não se limitando a, textos, gráficos, logotipos e software, são propriedade exclusiva da OmbudNow e são protegidos por leis internacionais de direitos autorais, marcas registradas e outras leis de propriedade intelectual."
      },
      {
        title: "6. Assinaturas e Pagamentos",
        content: "Determinadas funcionalidades requerem uma assinatura paga. Preços, condições de pagamento e detalhes dos planos estão descritos em nossa página de preços. Todas as taxas não são reembolsáveis, salvo indicação em contrário. A OmbudNow reserva-se o direito de modificar os preços com aviso prévio de 30 dias. Períodos de teste gratuito, quando oferecidos, são convertidos automaticamente em assinaturas pagas, a menos que cancelados antes do término do período de teste."
      },
      {
        title: "7. Proteção de Dados e Privacidade",
        content: "O uso da plataforma também é regido pela nossa Política de Privacidade. Estamos comprometidos em cumprir a Lei Geral de Proteção de Dados (LGPD) e outras regulamentações de privacidade aplicáveis. Ao utilizar a plataforma, você consente com o tratamento de dados conforme descrito em nossa Política de Privacidade."
      },
      {
        title: "8. Limitação de Responsabilidade",
        content: "A OmbudNow não será responsável por quaisquer danos indiretos, incidentais, especiais, consequenciais ou punitivos decorrentes do uso da plataforma. Nossa responsabilidade total por qualquer reclamação não excederá o valor pago por você nos doze meses anteriores à reclamação. Não garantimos serviço ininterrupto ou livre de erros."
      },
      {
        title: "9. Rescisão",
        content: "A OmbudNow reserva-se o direito de suspender ou encerrar seu acesso à plataforma a qualquer momento, com ou sem justa causa, mediante aviso prévio razoável. Após a rescisão, seu direito de uso da plataforma cessa imediatamente. As disposições que, por sua natureza, devam sobreviver à rescisão permanecerão em vigor."
      },
      {
        title: "10. Contato",
        content: "Para dúvidas sobre estes Termos de Uso, entre em contato pelo email: support@ombudnow.com"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white font-ibm" data-testid="terms-page">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={seoConfig.canonical}
        lang={currentLang}
        schemas={[breadcrumbSchema]}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={paths.home} className="flex items-center gap-3" data-testid="header-logo">
              <div className="w-9 h-9 bg-slate-900 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-slate-900 font-chivo tracking-tight">OMBUDNOW</span>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              <Link to={paths.about} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                {currentLang === 'en' ? 'About' : 'Sobre'}
              </Link>
              <Link to={currentLang === 'en' ? '/en/blog' : '/pt/blog'} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                Blog
              </Link>
              <Link to={paths.contact} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                {currentLang === 'en' ? 'Contact' : 'Contato'}
              </Link>
              <div className="flex items-center gap-1.5 text-slate-500 text-sm border-l border-slate-200 pl-6 ml-2">
                <Globe className="w-4 h-4" />
                <button
                  onClick={() => changeLanguage('pt')}
                  className={`px-1 ${currentLang === 'pt' ? 'text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
                  data-testid="lang-pt"
                >
                  PT
                </button>
                <span className="text-slate-300">/</span>
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-1 ${currentLang === 'en' ? 'text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
                  data-testid="lang-en"
                >
                  EN
                </button>
              </div>
            </nav>

            <Button
              className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-5"
              onClick={() => window.location.href = '/onboarding?plan=free'}
              data-testid="cta-start-free"
            >
              {currentLang === 'en' ? 'Start free' : 'Começar grátis'}
            </Button>
          </div>
        </div>
      </header>

      <main className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-amber-100" data-testid="terms-badge">
              <Scale className="w-4 h-4" />
              {content.badge}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-chivo tracking-tight" data-testid="terms-title">
              {content.title}
            </h1>
            <p className="text-slate-500">
              {content.lastUpdate}: {lastUpdate}
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="terms-content">
            {content.sections.map((section, index) => (
              <div key={index} className={`p-8 ${index !== content.sections.length - 1 ? 'border-b border-slate-100' : ''}`}>
                <h2 className="text-xl font-bold text-slate-900 mb-4 font-chivo">{section.title}</h2>
                <p className="text-slate-600 leading-relaxed">{section.content}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-10 text-center bg-slate-50 border border-slate-200 rounded-xl p-8" data-testid="terms-cta">
            <h3 className="text-xl font-bold text-slate-900 font-chivo mb-2">
              {currentLang === 'en' ? 'Ready to get started?' : 'Pronto para começar?'}
            </h3>
            <p className="text-slate-500 mb-6 text-sm">
              {currentLang === 'en' 
                ? 'Create your free account and set up your compliance channel in minutes.' 
                : 'Crie sua conta gratuita e configure seu canal de compliance em minutos.'}
            </p>
            <Button
              className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-8 py-2.5"
              onClick={() => window.location.href = '/onboarding?plan=free'}
              data-testid="terms-cta-button"
            >
              {currentLang === 'en' ? 'I agree — Start free' : 'Concordo — Começar grátis'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0B0F19] py-12 mt-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-chivo">OMBUDNOW</span>
            </div>
            <p className="text-slate-500 text-sm">
              © 2025 OmbudNow. {currentLang === 'en' ? 'All rights reserved.' : 'Todos os direitos reservados.'}
            </p>
            <div className="flex items-center gap-6">
              <Link to={paths.privacy} className="text-slate-500 hover:text-white text-sm transition-colors">
                {currentLang === 'en' ? 'Privacy' : 'Privacidade'}
              </Link>
              <Link to={paths.contact} className="text-slate-500 hover:text-white text-sm transition-colors">
                {currentLang === 'en' ? 'Contact' : 'Contato'}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
